import { Fragment } from 'react';
import { ArrowRight, ClipboardList, HeartPulse, Pill, Stethoscope, UserRound, type LucideIcon } from 'lucide-react';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { deltaToneClass, toneBadgeClass, type KpiTone } from '@/components/ui/KpiTile';
import { cn } from '@/lib/cn';
import { DashCard } from './DashCard';
import type { PipelineStatus, SummaryWindow } from './types';

// The five public-facing roles of reports.js's ROLE_STAGES, in pipeline order.
// "unassigned" is deliberately not a stage here: pipeline-status counts a visit
// under it when it passed token assignment at all, which is nearly every visit
// — the live "nobody picked" figure lives in the queue, not this card.
const STAGES: Array<{ role: string; label: string; icon: LucideIcon; tone: KpiTone }> = [
  { role: 'registration', label: 'Intake', icon: UserRound, tone: 'blue' },
  { role: 'nurse', label: 'Nurse', icon: HeartPulse, tone: 'green' },
  { role: 'assessment', label: 'Assessment', icon: ClipboardList, tone: 'violet' },
  { role: 'consultant', label: 'Consultant', icon: Stethoscope, tone: 'orange' },
  { role: 'pharmacy', label: 'Pharmacy / Exit', icon: Pill, tone: 'teal' },
];

export const WINDOW_OPTIONS: Array<{ value: SummaryWindow; label: string }> = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
];

/**
 * OPD Workflow Status — docs/design/dashboard region 6 and section F.
 *
 * The stages are ONE grid of nine fixed tracks (five shrinking stage columns,
 * four 22px arrow columns), so they can never wrap onto a second row the way
 * the old fixed-86px-box flex-wrap row did on smaller screens. Labels sit in
 * a fixed two-line box. Measured: all five fit, labels included, down to a
 * 440px card; the dashboard reserves 460px for it, and below that the layout
 * switches to the older arrangement (Dashboard.tsx). Under 440px (a phone)
 * the strip scrolls sideways inside the card rather than wrapping.
 */
export function WorkflowCard({ status, window, onWindowChange }: {
  status: PipelineStatus | null;
  window: SummaryWindow;
  onWindowChange: (w: SummaryWindow) => void;
}) {
  const total = status?.total ?? 0;
  const completed = status?.completed ?? 0;
  const pct = total ? Math.round((completed / total) * 100) : 0;
  const byRole = new Map((status?.roles ?? []).map(r => [r.role, r]));
  const periodLabel = WINDOW_OPTIONS.find(o => o.value === window)?.label ?? '';

  return (
    <DashCard
      title="OPD Workflow Status"
      action={
        <select
          value={window}
          onChange={e => onWindowChange(e.target.value as SummaryWindow)}
          aria-label="Period"
          className="w-auto shrink-0 cursor-pointer rounded-md border border-control-line bg-card px-2 py-1 text-[11px] text-ink"
        >
          {WINDOW_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      }
    >
      <div className="overflow-x-auto">
        <div className="mt-4 grid min-w-[420px] grid-cols-[minmax(0,1fr)_22px_minmax(0,1fr)_22px_minmax(0,1fr)_22px_minmax(0,1fr)_22px_minmax(0,1fr)] items-start text-center">
          {STAGES.map((s, i) => {
            const r = byRole.get(s.role);
            const Icon = s.icon;
            return (
              <Fragment key={s.role}>
                <div className="min-w-0">
                  <div className={cn('mx-auto flex h-11 w-11 items-center justify-center rounded-[9px]', toneBadgeClass(s.tone))}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className="mt-2 flex h-7 items-center justify-center text-[11px] leading-tight text-ink">{s.label}</div>
                  <div className="text-[19px] font-bold leading-tight text-ink">{r?.count ?? 0}</div>
                  <div className={cn('mt-1 text-[11px] font-medium', r?.delta == null ? 'invisible' : deltaToneClass({ value: r.delta }))}>
                    {r?.delta != null ? `${r.delta > 0 ? '↑' : r.delta < 0 ? '↓' : '–'} ${Math.abs(r.delta)}%` : '0%'}
                  </div>
                </div>
                {i < STAGES.length - 1 && <ArrowRight aria-hidden className="mx-auto mt-[15px] h-3.5 w-3.5 text-ink-muted/60" />}
              </Fragment>
            );
          })}
        </div>
      </div>
      <div className="mt-auto pt-3.5">
        <div className="flex justify-between border-t border-line pt-[9px] text-[11.5px] text-ink">
          <span>Total OPD visits ({periodLabel.toLowerCase()}): <b className="font-semibold text-success-ink">{total}</b></span>
          <b className="font-semibold text-success-ink">Completed: {completed} ({pct}%)</b>
        </div>
        <ProgressBar pct={pct} className="mt-[9px]" label="Completed OPD visits" />
      </div>
    </DashCard>
  );
}
